/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    ContentType,
    VodOttActionDetailsList,
    WatchAction,
    WatchFromSoCuAction,
    WatchFromSoCuActionKernel,
} from 'client-code/action-details';
import { ActionGroup } from 'client-code/actions';
import { PreviewType, ScreenType } from 'client-code/previews';

import {
    APPLICATION_FEATURE_AREA,
    LEFT_QUOTE,
    MILLISECONDS_IN_A_SECOND,
    RIGHT_QUOTE,
} from '../../../config';
import { AppUtils } from '../../../core/AppUtils';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { Channel } from '../../../core/entities/Channel';
import { NFREvents } from '../../../core/enums';
import { AppError } from '../../../core/error';
import { NdvrInteractor, ServicesClientImpl, VodInteractor } from '../../../core/interactors';
import { LocaleKeys } from '../../../core/locale';
import { Locale } from '../../../core/locale/Locale';
import { Log, NFRLoggings, OperationsLogger } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { PlaybackContentType } from '../../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../../core/playback/impls';
import { TOfferPreviewCustomData, TOverlayOptions } from '../../../core/types';
import { BrandingUiUtility } from '../../../core/utils/BrandingUiUtility';
import { UniqueIdentifier } from '../../../core/utils/UniqueIdentifier';
import { IPlayable } from '../../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { guideHideSpinner, guideShowSpinner } from '../../guide/guide.reducers';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import {
    resetPlaybackParamsVideoplayer,
    startVideoPlayback,
} from '../../videoplayer/videoplayer.reducers';
import { hideInfoBanners } from '../../videoplayerui/infobanner/infobanner.reducers';
import { OverlayTypes } from '../OverlayTypes';
import { showSocuErrorOverlay } from '../socuerroroverlay/socuerroroverlay.reducres';
import { isJumpChannelCcbSwitchEnabled } from '../../../core/utils/ccb/CcbSwitchHelper';

const GLOBAL_MOUSE_TRAP: string = 'HideVodResumeStartOverOverlay';

const TAG: string = 'VodResumeStartOverOverlay';

const deriveLastViewedPosition = (
    position: number | undefined,
    duration: number | undefined,
): number => {
    if (!position || (duration && position >= duration)) {
        return 0;
    }
    return position;
};

export const showVodResumeStartOverOverlay =
    (offerPreview: TOfferPreviewCustomData, hasDimmer: boolean = true) =>
    (dispatch: Function) => {
        dispatch(
            setScreenTaskController(ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS),
        );
        const socuDataModelImpl = PlaybackDataModelImpl.getInstance();
        const { isCatchUp } = socuDataModelImpl.getNewSocuAction()?.kernel;
        if (!offerPreview || !Object.keys(offerPreview)) return;
        let titleText: string =
            offerPreview.contentPreview?.title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
        let subtitleText: string = '';

        if (offerPreview.contentPreview?.previewType === PreviewType.EpisodePreview) {
            const { episodeNumber, seasonNumber, episodeTitle } = offerPreview.contentPreview;
            const seasonEpisodeText: string = AppUtils.generateSeasonEpisodeText(
                seasonNumber || 0,
                episodeNumber || 0,
            );
            if (seasonEpisodeText || episodeTitle) {
                subtitleText = `${seasonEpisodeText} ${LEFT_QUOTE}${
                    episodeTitle || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE)
                }${RIGHT_QUOTE}`;
            }
        } else if (offerPreview.contentPreview?.previewType === PreviewType.MoviePreview) {
            const { movieYear } = offerPreview.contentPreview;
            const movieYearText: string = movieYear ? ` (${movieYear})` : '';
            titleText = `${titleText}${movieYearText}`;
        }

        const overlayOptions: Array<TOverlayOptions> = [
            {
                text: `${Locale.getString(LocaleKeys.OVERLAY_OPTION_RESUME_PLAYING_TEXT)}`,
                value: isCatchUp
                    ? PlaybackContentType.CATCHUP_RESUME
                    : PlaybackContentType.STARTOVER_RESUME,
            },
            {
                text: `${Locale.getString(LocaleKeys.OVERLAY_OPTION_START_OVER_FROM_BEGINNING_TEXT)}`,
                value: isCatchUp
                    ? PlaybackContentType.CATCHUP_BEGINNING
                    : PlaybackContentType.STARTOVER_BEGINNING,
            },
        ];
        const overlayProps: IOverlayDialog = {
            hasDimmer,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: overlayOptions,
            overlayType: OverlayTypes.VOD_RESUME_START_OVER,
            subtitle: subtitleText,
            title: titleText,
        };
        dispatch(showOverlayDialog(overlayProps));
    };

export const onKeyBackVodResumeStartOverOverlay = () => (dispatch: Function) => {
    PlaybackDataModelImpl.getInstance().resetNewPlaybackData();
    dispatch(hideOverlayDialog());
};

export const onKeyOkVodResumeStartOverOverlay = () => (dispatch: Function, getState: Function) => {
    const { gridGuide, guideHighlight, overlayHighlight, overlayDialog } = getState();

    const { channels } = gridGuide;
    const { verticalPosition } = guideHighlight;
    const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
    const { options } = overlayDialog.uiProperties;

    const channel: Channel = channels[verticalPosition];
    const playbackContentType: PlaybackContentType =
        options[overlayHighlightVerticalPosition].value;

    const playable: IPlayable = { channel, playbackContentType };

    PlaybackDataModelImpl.getInstance().setPlaybackData(playbackContentType);
    dispatch(hideOverlayDialog());
    dispatch(startVideoPlayback(playable));
};

export const onKeyOkVodResumeStartOverOverlayFromLiveTv =
    () => (dispatch: Function, getState: Function) => {
        const { overlayHighlight, overlayDialog } = getState();
        const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
        const { options } = overlayDialog.uiProperties;
        const playbackContentType: PlaybackContentType =
            options[overlayHighlightVerticalPosition].value;

        PlaybackDataModelImpl.getInstance().setPlaybackData(playbackContentType);
        dispatch(hideOverlayDialog());
        dispatch(resetPlaybackParamsVideoplayer());
        VideoPlaybackController.getInstance().startPlayback({
            channel: VideoPlaybackController.getInstance().getPlayable()?.channel || <Channel>{},
            playbackContentType,
        });
    };

export const onMouseClickVodResumeStartOverOverlay =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;

        if (value === GLOBAL_MOUSE_TRAP) {
            dispatch(hideOverlayDialog());
            return;
        }

        dispatch(onKeyOkVodResumeStartOverOverlay());
    };

export const prepareVodResumeStartOverOverlayFromGuide =
    (channel: Channel, offerPreview: TOfferPreviewCustomData) => async (dispatch: Function) => {
        dispatch(guideShowSpinner());
        if (!offerPreview || !Object.keys(offerPreview)) {
            dispatch(guideHideSpinner());
            return;
        }
        const playbackDataModelImpl = PlaybackDataModelImpl.getInstance();
        const { isCatchUp, isStartOver } = playbackDataModelImpl.getNewSocuAction()?.kernel;
        const networkCallId = UniqueIdentifier.getNextOttActionDetailsNetworkCallId();
        try {
            const { watchAction, actionDetailsNetworkCallId } =
                await playbackDataModelImpl.fetchLastSavedPosition(
                    ScreenType.GuideRecordOverlay,
                    networkCallId,
                );
            if (
                actionDetailsNetworkCallId !== UniqueIdentifier.getOttActionDetailsNetworkCallId()
            ) {
                Log.i(TAG, 'Network call id mismatched.');
                dispatch(guideHideSpinner());
                return;
            }
            const { savedPosition, duration } = (watchAction as WatchFromSoCuAction)
                .kernel as WatchFromSoCuActionKernel;
            const derivedPosition = deriveLastViewedPosition(savedPosition, duration);
            if (derivedPosition) {
                dispatch(guideHideSpinner());
                dispatch(showVodResumeStartOverOverlay(offerPreview));
            } else {
                const playbackContentType: PlaybackContentType = isCatchUp
                    ? PlaybackContentType.CATCHUP_BEGINNING
                    : PlaybackContentType.STARTOVER_BEGINNING;
                playbackDataModelImpl.setPlaybackData(playbackContentType);
                dispatch(guideHideSpinner());
                // We create a copy of the channel because we want to trigger the effect in
                // FullScreenPlayback component even if the channel does not change
                const socuPlayable: IPlayable = {
                    channel,
                    playbackContentType,
                };
                dispatch(startVideoPlayback(socuPlayable));
            }
        } catch (error: any) {
            const services = new ServicesClientImpl();
            (error as AppError).setDescription(
                BrandingUiUtility.formatDescription((error as AppError).getDescription(), {
                    CATCHUP_NAME: isStartOver
                        ? services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER)
                        : services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP),
                    ERROR_CODE: (error as AppError).getCode(),
                }),
            );
            OperationsLogger.logUIErrorCode(error?.code);
            NFRLoggings.log(NFREvents.FAILURE_LOADING_VOD_OTT_ACTION_DETAILS, error);
            Diagnostics.error(TAG, 'Error while fetching vodOttActionDetails');
            Diagnostics.error(TAG, 'stacktrace', error);
            Log.e(TAG, 'Error while fetching vodOttActionDetails');
            Log.e(TAG, 'stacktrace', error);
            dispatch(guideHideSpinner());
            dispatch(showSocuErrorOverlay(error));
        }
    };

export const prepareVodResumeStartOverOverlayFromLiveTv =
    () => async (dispatch: Function, getState: Function) => {
        dispatch(
            setScreenTaskController(
                ScreenTaskControllerTypes.PREPARING_VOD_RESUME_START_OVER_OVERLAY_TASKS,
            ),
        );
        const channel = VideoPlaybackController.getInstance().getPlayable()?.channel;
        const { airingOffers, offerPreview } = VideoPlaybackController.getInstance().getMetadata();
        if (!offerPreview || !Object.keys(offerPreview)) return;
        if (!airingOffers || airingOffers.length === 0) return;
        // TODO: We need to find better approach to handle errors
        const sThrowable: string = 'fetchActionList network call id mis-matched';
        const services = new ServicesClientImpl();
        // TODO: We need to use or eleminate socu data model. Below calculation should be part of
        // model.
        const currentTime = Date.now();
        const startTime = new Date(airingOffers[0].startTime).getTime();
        const programEndTime = startTime + airingOffers![0].duration * MILLISECONDS_IN_A_SECOND;
        const currentlyAiring = startTime <= currentTime && currentTime < programEndTime;

        NFRLoggings.log(NFREvents.START_LOADING_ACTIONS_LIST);

        const networkCallId = UniqueIdentifier.getNextActionsNetworkCallIdForLiveTv();
        const oPlaybackDataModelImpl = PlaybackDataModelImpl.getInstance();
        oPlaybackDataModelImpl
            .fetchActionV2List({
                channelNumber: channel?.channelNumber ?? '',
                groups: [ActionGroup.Watch],
                offerId: airingOffers[0].offerId,
                screenType: APPLICATION_FEATURE_AREA.PLAYER,
                actionsNetworkCallId: networkCallId,
            })
            .then((actionList) => {
                return actionList.actionsNetworkCallId === networkCallId;
            })
            .then((status) => {
                if (status) {
                    return oPlaybackDataModelImpl.fetchLastSavedPosition(ScreenType.LiveTvBanner);
                }
                throw new Error(sThrowable);
            })
            .then(({ watchAction }) => {
                const { screenTaskController, previousScreenTaskController } = getState().screens;
                if (
                    screenTaskController ===
                    ScreenTaskControllerTypes.PREPARING_VOD_RESUME_START_OVER_OVERLAY_TASKS
                ) {
                    dispatch(setScreenTaskController(previousScreenTaskController));
                } else {
                    Log.i(TAG, 'Discard resume overlay');
                    return;
                }
                dispatch(resetPlaybackParamsVideoplayer());
                dispatch(hideInfoBanners());
                const { savedPosition, duration } = (watchAction as WatchFromSoCuAction)
                    .kernel as WatchFromSoCuActionKernel;
                const derivedPosition = deriveLastViewedPosition(savedPosition, duration);
                if (derivedPosition) {
                    dispatch(showVodResumeStartOverOverlay(offerPreview, false));
                    return true;
                }
                const { isCatchUp } = oPlaybackDataModelImpl.getNewSocuAction()?.kernel;
                const playbackContentType: PlaybackContentType = isCatchUp
                    ? PlaybackContentType.CATCHUP_BEGINNING
                    : PlaybackContentType.STARTOVER_BEGINNING;
                oPlaybackDataModelImpl.setPlaybackData(playbackContentType);
                VideoPlaybackController.getInstance().startPlayback({
                    channel: channel || <Channel>{},
                    playbackContentType,
                });
                return false;
            })
            .catch((error) => {
                const { screenTaskController, previousScreenTaskController } = getState().screens;
                if (
                    screenTaskController ===
                    ScreenTaskControllerTypes.PREPARING_VOD_RESUME_START_OVER_OVERLAY_TASKS
                ) {
                    dispatch(setScreenTaskController(previousScreenTaskController));
                } else {
                    Log.i(TAG, 'Discard resume overlay');
                    return;
                }
                const errorMsg: string = error.message;
                if (!errorMsg?.includes(sThrowable)) {
                    (error as AppError).setDescription(
                        BrandingUiUtility.formatDescription((error as AppError).getDescription(), {
                            CATCHUP_NAME: currentlyAiring
                                ? services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER)
                                : services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP),
                            ERROR_CODE: (error as AppError).getCode(),
                        }),
                    );
                    dispatch(showSocuErrorOverlay(error, false));
                }
                Log.e(TAG, 'stacktrace', error);
                Diagnostics.error(TAG, 'stacktrace', error);
            });
    };

/**
 * This file is totally messed up with different mechanisms to show the resume/start over overlay
 * and the code is not well structured. It is hard to understand the flow of the code.
 * Below is the refactored code to show the resume/start over overlay for VOD OTT content.
 */

const showVodResumeStartOverOverlayForWatchAction =
    (watchAction: WatchAction, isStartOver?: boolean) => (dispatch: Function) => {
        const { actionDetails } = PlaybackDataModelImpl.getInstance().getActionDetailsList();

        // generating title for the overlay
        let title: string = actionDetails.title || '';
        if (actionDetails.contentType === ContentType.Episode) {
            const { episodeNumber, seasonNumber, episodeTitle } = actionDetails;
            const seasonEpisodeText: string = AppUtils.generateSeasonEpisodeText(
                seasonNumber && seasonNumber > -1 ? seasonNumber : 0,
                episodeNumber && episodeNumber > -1 ? episodeNumber : 0,
            );
            title += seasonEpisodeText.length ? ` - ${seasonEpisodeText} ` : '';
            title += ` ${LEFT_QUOTE}${
                episodeTitle || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE)
            }${RIGHT_QUOTE}`;
        } else if (actionDetails.contentType === ContentType.Movie) {
            const { movieYear } = actionDetails;
            const movieYearText: string = movieYear ? ` (${movieYear})` : '';
            title = `${title}${movieYearText}`;
        }

        // generating options for the overlay with optionType and additional data like channel
        const overlayOptions: Array<TOverlayOptions> = [
            {
                text: `${Locale.getString(LocaleKeys.OVERLAY_OPTION_RESUME_PLAYING_TEXT)}`,
                value: {
                    optionType: isStartOver
                        ? PlaybackContentType.STARTOVER_RESUME
                        : PlaybackContentType.CATCHUP_RESUME,
                    channel: (
                        (watchAction as WatchFromSoCuAction).kernel as WatchFromSoCuActionKernel
                    ).channel,
                },
            },
            {
                text: `${Locale.getString(LocaleKeys.OVERLAY_OPTION_START_OVER_FROM_BEGINNING_TEXT)}`,
                value: {
                    optionType: isStartOver
                        ? PlaybackContentType.STARTOVER_BEGINNING
                        : PlaybackContentType.CATCHUP_BEGINNING,
                    channel: (
                        (watchAction as WatchFromSoCuAction).kernel as WatchFromSoCuActionKernel
                    ).channel,
                },
            },
        ];
        const overlayProps: IOverlayDialog = {
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: overlayOptions,
            overlayType: OverlayTypes.VOD_RESUME_START_OVER,
            title,
        };
        // displaying the overlay with required props
        dispatch(
            setScreenTaskController(ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS),
        );
        dispatch(showOverlayDialog(overlayProps));
    };

const startSoCuPlayback =
    (optionType: PlaybackContentType, channelNumber?: string) => async (dispatch: Function) => {
        if (channelNumber) {
            const playbackContentType: PlaybackContentType = optionType;
            const playbackDataModelImpl = PlaybackDataModelImpl.getInstance();
            playbackDataModelImpl.setPlaybackData(playbackContentType);
            const services = new VodInteractor();
            const getChannelFromChannelNumber = isJumpChannelCcbSwitchEnabled()
                ? services.getChannelFromChannelNumberV2
                : services.getChannelFromChannelNumber;
            const channelObj = await getChannelFromChannelNumber(channelNumber);
            // We create a copy of the channel because we want to trigger the effect in
            // FullScreenPlayback component even if the channel does not change
            const socuPlayable: IPlayable = {
                channel: channelObj,
                playbackContentType,
            };
            dispatch(startVideoPlayback(socuPlayable));
        } else {
            Log.e(TAG, 'Channel number is not available to start socu playback');
        }
    };

export const onKeyOkVodResumeStartOverOverlayForWatchAction =
    () => async (dispatch: Function, getState: Function) => {
        const { overlayHighlight, overlayDialog } = getState();
        const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
        const { options } = overlayDialog.uiProperties;
        const { optionType, channel } = options[overlayHighlightVerticalPosition].value;
        dispatch(hideOverlayDialog());
        dispatch(startSoCuPlayback(optionType, channel.channelNumber));
    };

export const onMouseClickVodResumeStartOverOverlayForWatchAction =
    () => async (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;

        if (value === GLOBAL_MOUSE_TRAP) {
            dispatch(hideOverlayDialog());
            return;
        }

        dispatch(onKeyOkVodResumeStartOverOverlayForWatchAction());
    };

/**
 * this function prepares the resume/start over overlay for the watch action.
 * if the saved position is available then it shows the overlay otherwise it starts the
 * SoCu playback
 * @param watchAction WatchAction
 * @param isStartOver boolean
 * @returns
 */
export const prepareVodResumeStartOverOverlayForWatchAction =
    (watchAction: WatchAction, isStartOver?: boolean) => async (dispatch: Function) => {
        const { savedPosition, channel, duration } = (watchAction as WatchFromSoCuAction)
            .kernel as WatchFromSoCuActionKernel;
        const derivedPosition = deriveLastViewedPosition(savedPosition, duration);
        if (derivedPosition) {
            dispatch(showVodResumeStartOverOverlayForWatchAction(watchAction, isStartOver));
        } else {
            const playbackContentType: PlaybackContentType = isStartOver
                ? PlaybackContentType.STARTOVER_BEGINNING
                : PlaybackContentType.CATCHUP_BEGINNING;
            dispatch(startSoCuPlayback(playbackContentType, channel.channelNumber));
        }
    };

/**
 * VOD Resume overlay with resume/start over options.
 * This function provides a dedicated overlay for VOD content playback options.
 */
export const showVodResumeOverlay = () => (dispatch: Function) => {
    try {
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS));

        const playbackDataModelImpl = PlaybackDataModelImpl.getInstance();

        // Get action details from PlaybackDataModelImpl
        const { actionDetails } = playbackDataModelImpl.getActionDetailsList();

        if (!actionDetails) {
            Log.w(TAG, 'No action details available for VOD resume overlay');
            return;
        }

        const vodActionDetails = actionDetails as VodOttActionDetailsList;

        let titleText: string = '';
        const { contentType, title, movieYear, seasonNumber, episodeNumber, episodeTitle } =
            vodActionDetails;

        // Generate title based on content type
        if (contentType === ContentType.Movie) {
            // For a movie: "<movie title> (<year>)"
            const movieTitle = title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
            titleText = movieYear ? `${movieTitle} (${movieYear})` : movieTitle;
        } else if (contentType === ContentType.Episode) {
            // For a single episode: "<series title> - <sea/ep> <episode title>"
            const seriesTitle = title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
            const seasonEpisodeText = AppUtils.generateSeasonEpisodeText(
                seasonNumber || 0,
                episodeNumber || 0,
            );
            const episodeTitleText = episodeTitle || '';

            if (seasonEpisodeText && episodeTitleText) {
                titleText = `${seriesTitle} - ${seasonEpisodeText} ${episodeTitleText}`;
            } else if (seasonEpisodeText) {
                titleText = `${seriesTitle} - ${seasonEpisodeText}`;
            } else if (episodeTitleText) {
                titleText = `${seriesTitle} - ${episodeTitleText}`;
            } else {
                titleText = seriesTitle;
            }
        } else {
            // For music video or non-episodic show: use the title as is
            titleText = title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
        }

        // Create overlay options - always Resume and Start Over for VOD content
        const overlayOptions: Array<TOverlayOptions> = [
            {
                text: `${Locale.getString(LocaleKeys.OVERLAY_OPTION_RESUME_PLAYING_TEXT)}`,
            },
            {
                text: `${Locale.getString(LocaleKeys.OVERLAY_OPTION_START_OVER_FROM_BEGINNING_TEXT)}`,
            },
        ];

        const overlayProps: IOverlayDialog = {
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: overlayOptions,
            overlayType: OverlayTypes.VOD_RESUME_START_OVER,
            subtitle: '',
            title: titleText,
        };

        dispatch(showOverlayDialog(overlayProps));

        // Log successful overlay display
        Log.i(TAG, 'VOD Resume overlay displayed successfully');
        NFRLoggings.log(NFREvents.START_LOADING_VOD_OTT_ACTION_DETAILS);
    } catch (error: any) {
        Log.e(TAG, 'Error displaying VOD Resume overlay', error);
        Diagnostics.error(TAG, 'Error displaying overlay', error);
    }
};
